import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, Calendar, Download, Search, Archive, RotateCcw, MessageCircle, X, Send, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSettings } from '@/context/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEnquiries = () => {
  const { settings } = useSettings();
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'
  // Feedback modal state
  const [feedbackModal, setFeedbackModal] = useState(null); // { enquiry } or null
  const [feedbackForm, setFeedbackForm] = useState({
    feedback_type: 'Booking Feedback',
    custom_type: '',
    title: 'We\'d love your feedback',
    project_name: '',
    whatsapp_number: '',
  });
  const [feedbackResult, setFeedbackResult] = useState(null); // { id, link, whatsappUrl }
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchEnquiries();
    fetchProjects();
  }, []);

  useEffect(() => {
    filterEnquiries();
  }, [searchTerm, filterProject, enquiries, activeTab]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchEnquiries = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      setLoading(true);
      const response = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(response.data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const filterEnquiries = () => {
    let filtered = [...enquiries];
    
    // Filter by archive status
    if (activeTab === 'active') {
      filtered = filtered.filter(e => !e.is_archived);
    } else {
      filtered = filtered.filter(e => e.is_archived);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e => 
        e.name?.toLowerCase().includes(term) ||
        e.email?.toLowerCase().includes(term) ||
        e.phone?.includes(term)
      );
    }
    
    if (filterProject) {
      filtered = filtered.filter(e => e.interested_project === filterProject);
    }
    
    setFilteredEnquiries(filtered);
  };

  const toggleArchive = async (enquiryId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.put(`${API}/enquiries/${enquiryId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message);
      fetchEnquiries();
    } catch (error) {
      console.error('Error archiving enquiry:', error);
      toast.error('Failed to update enquiry');
    }
  };

  const openFeedbackModal = (enquiry) => {
    setFeedbackModal({ enquiry });
    setFeedbackForm({
      feedback_type: 'Booking Feedback',
      custom_type: '',
      title: 'We\'d love your feedback',
      project_name: enquiry.interested_project || '',
      whatsapp_number: enquiry.phone || settings.admin_whatsapp_number || '',
    });
    setFeedbackResult(null);
  };

  const closeFeedbackModal = () => {
    setFeedbackModal(null);
    setFeedbackResult(null);
  };

  const generateFeedbackLink = async (e) => {
    e.preventDefault();
    if (!feedbackModal) return;
    const enquiry = feedbackModal.enquiry;
    const finalType =
      feedbackForm.feedback_type === 'Custom'
        ? (feedbackForm.custom_type || '').trim()
        : feedbackForm.feedback_type;
    if (!finalType) {
      toast.error('Please enter a custom feedback type');
      return;
    }
    if (!feedbackForm.project_name.trim()) {
      toast.error('Project name is required');
      return;
    }
    setGenerating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${API}/feedback-requests`,
        {
          enquiry_id: enquiry.id,
          customer_name: enquiry.name,
          project_name: feedbackForm.project_name.trim(),
          whatsapp_number: feedbackForm.whatsapp_number.trim(),
          feedback_type: finalType,
          title: feedbackForm.title.trim() || 'We\'d love your feedback',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const id = res.data.id;
      const link = `${window.location.origin}/feedback/${id}`;
      const num = (feedbackForm.whatsapp_number || '').replace(/[^0-9]/g, '');
      const msg =
        `Hi ${enquiry.name}! We'd love your feedback on ${feedbackForm.project_name}. ` +
        `Please take a minute to share your experience here: ${link}\n\nThank you,\nInfinity Group`;
      const whatsappUrl = num
        ? `https://wa.me/${num}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      setFeedbackResult({ id, link, whatsappUrl });
      toast.success('Feedback link generated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate link');
    } finally {
      setGenerating(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Name', 'Phone', 'Email', 'Interested Property', 'BHK Type', 
      'Budget', 'Source', 'Referred By', 'Message', 'Date', 'Status'
    ];
    
    const csvData = filteredEnquiries.map(e => [
      e.name || '',
      e.phone || '',
      e.email || '',
      e.interested_project || '',
      e.bhk_type || '',
      e.budget || '',
      e.source || '',
      e.referred_by || '',
      (e.message || '').replace(/,/g, ';').replace(/\n/g, ' '),
      e.created_at ? new Date(e.created_at).toLocaleDateString('en-IN') : '',
      e.is_archived ? 'Archived' : 'Active'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `enquiries_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${filteredEnquiries.length} enquiries to CSV`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const activeCount = enquiries.filter(e => !e.is_archived).length;
  const archivedCount = enquiries.filter(e => e.is_archived).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_34f43037-f49f-4896-a120-3461406a9d35/artifacts/wh0yojmq_e30d7691-c116-46a6-b645-fa81d1964b10-removebg-preview.png" 
                alt="Infinity Group" 
                className="h-20 w-auto"
              />
              <span className="text-xl font-bold text-gray-900">Infinity Group</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-enquiries-heading">
              Customer Enquiries
            </h1>
            <p className="text-gray-500 mt-1">{filteredEnquiries.length} leads in {activeTab} section</p>
          </div>
          <Button 
            onClick={exportToCSV} 
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            disabled={filteredEnquiries.length === 0}
          >
            <Download size={18} />
            Export to CSV
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'active' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Active Leads ({activeCount})
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'archived' 
                ? 'bg-gray-700 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Archive size={16} />
            Archived ({archivedCount})
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Properties</option>
              {projects.map((project) => (
                <option key={project.id} value={project.name}>{project.name}</option>
              ))}
              <option value="General Enquiry">General Enquiry</option>
            </select>
            <button
              onClick={() => { setSearchTerm(''); setFilterProject(''); }}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          </div>
        ) : filteredEnquiries.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center" data-testid="no-enquiries">
            <p className="text-gray-600 text-lg">
              {enquiries.length === 0 ? 'No enquiries yet.' : 'No enquiries match your filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEnquiries.map((enquiry) => (
              <div
                key={enquiry.id}
                className={`bg-white rounded-xl p-6 shadow-md ${enquiry.is_archived ? 'opacity-75' : ''}`}
                data-testid={`enquiry-card-${enquiry.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{enquiry.name}</h3>
                    <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                      <Calendar size={14} />
                      {formatDate(enquiry.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      enquiry.is_archived 
                        ? 'bg-gray-100 text-gray-600' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {enquiry.is_archived ? 'Archived' : 'New Lead'}
                    </span>
                    <button
                      onClick={() => openFeedbackModal(enquiry)}
                      className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Generate Feedback Link"
                      data-testid={`feedback-link-btn-${enquiry.id}`}
                    >
                      <MessageCircle size={18} />
                    </button>
                    <button
                      onClick={() => toggleArchive(enquiry.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        enquiry.is_archived
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                      title={enquiry.is_archived ? 'Restore' : 'Archive'}
                    >
                      {enquiry.is_archived ? <RotateCcw size={18} /> : <Archive size={18} />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone size={16} className="text-gray-400" />
                    <a href={`tel:${enquiry.phone}`} className="hover:text-blue-600">
                      {enquiry.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail size={16} className="text-gray-400" />
                    <a href={`mailto:${enquiry.email}`} className="hover:text-blue-600">
                      {enquiry.email}
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                  <div>
                    <p className="text-gray-500">Interested Property</p>
                    <p className="font-medium text-gray-900">{enquiry.interested_project || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">BHK Type</p>
                    <p className="font-medium text-gray-900">{enquiry.bhk_type || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Budget</p>
                    <p className="font-medium text-gray-900">{enquiry.budget || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Source</p>
                    <p className="font-medium text-gray-900">{enquiry.source || '-'}</p>
                  </div>
                </div>

                {enquiry.referred_by && (
                  <div className="mb-3 text-sm">
                    <span className="text-gray-500">Referred By: </span>
                    <span className="font-medium text-gray-900">{enquiry.referred_by}</span>
                  </div>
                )}

                {enquiry.message && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Message:</p>
                    <p className="text-gray-700">{enquiry.message}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feedback Link Modal */}
      {feedbackModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {feedbackResult ? 'Share Feedback Link' : 'Generate Feedback Link'}
              </h2>
              <button onClick={closeFeedbackModal} className="text-gray-500 hover:text-gray-800">
                <X size={20} />
              </button>
            </div>

            {!feedbackResult ? (
              <form onSubmit={generateFeedbackLink} className="p-5 space-y-4">
                <p className="text-sm text-gray-600">
                  Sending to: <strong>{feedbackModal.enquiry.name}</strong> ({feedbackModal.enquiry.phone})
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feedback Type *</label>
                  <select
                    value={feedbackForm.feedback_type}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Booking Feedback">Booking Feedback</option>
                    <option value="Handover Feedback">Handover Feedback</option>
                    <option value="Custom">Custom...</option>
                  </select>
                </div>

                {feedbackForm.feedback_type === 'Custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Type *</label>
                    <input
                      type="text"
                      value={feedbackForm.custom_type}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, custom_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., Construction Quality Feedback"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Form Title</label>
                  <input
                    type="text"
                    value={feedbackForm.title}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                  <input
                    type="text"
                    value={feedbackForm.project_name}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, project_name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Shown to the customer & on the homepage testimonial.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={feedbackForm.whatsapp_number}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, whatsapp_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="91XXXXXXXXXX (with country code)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to use the enquiry phone, or enter a different number to share to.
                  </p>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={closeFeedbackModal} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={generating}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="generate-feedback-btn"
                  >
                    {generating ? 'Generating...' : 'Generate Link'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="p-5 space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  Link generated. Share it with your customer below.
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feedback Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={feedbackResult.link}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(feedbackResult.link);
                        toast.success('Link copied');
                      }}
                      className="px-3 py-2 bg-gray-100 text-gray-800 hover:bg-gray-200 rounded-lg text-sm flex items-center gap-1"
                    >
                      <Copy size={14} /> Copy
                    </button>
                  </div>
                </div>
                <a
                  href={feedbackResult.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium"
                  data-testid="share-feedback-whatsapp-btn"
                >
                  <Send size={16} className="inline mr-2" /> Share on WhatsApp
                </a>
                <p className="text-xs text-gray-500 text-center">
                  Manage all feedback at <Link to="/admin/feedback" className="text-blue-600 underline">Admin → Feedback</Link>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEnquiries;